import type React from 'react';
import channelLogisticsLogo from '@/assets/icons/channel-logistics-logo.png';
import emailIcon from '@/assets/icons/email.png';
import lockIcon from '@/assets/icons/lock.png';
import { LoadingSpinner } from '@/components/atoms/primitives';
import type { LoginStageProps } from './LoginStage.types';

export const LoginStage: React.FC<LoginStageProps> = ({ onSubmit, error, loading, onForgotPassword }) => {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = event.target as typeof event.target & {
      email: { value: string };
      password: { value: string };
    };
    onSubmit(target.email.value, target.password.value);
  };

  return (
    <div className="flex flex-col items-center justify-center min-w-[600px] max-w-[600px] flex-shrink-0 h-full overflow-hidden relative">
      <div
        className="absolute right-4 bottom-4 w-[100px] h-[70px] bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${channelLogisticsLogo})` }}
      />
      <form className="flex flex-col w-[400px] h-[500px] overflow-y-auto" onSubmit={handleSubmit} noValidate>
        <header>
          <h1 className="text-2xl text-white font-bold mb-8">Log in</h1>
        </header>

        {error && (
          <div
            id="login-error"
            className="text-brand-error font-bold -mt-4 mb-4 text-center"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            {error}
          </div>
        )}

        <section aria-labelledby="login-form-heading">
          <h2 id="login-form-heading" className="sr-only">
            Login credentials
          </h2>
          <fieldset className="border-0 p-0 m-0">
            <legend className="sr-only">Enter your login credentials</legend>

            <div>
              <label htmlFor="login-email" className="text-base text-white font-bold mb-2.5 block">
                Email address
              </label>
              <div className="flex items-center max-w-full h-[50px] border border-gray-500 rounded-2xl px-4 mb-6">
                <span
                  className="w-6 h-6 bg-cover filter invert-[90%]"
                  style={{ backgroundImage: `url(${emailIcon})` }}
                  aria-hidden="true"
                />
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  className="w-[300px] ml-4 bg-transparent border-none text-white focus:outline-none"
                  placeholder="test@email.com"
                  required
                  autoComplete="email"
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="text-base text-white font-bold mb-2.5 block">
                Password
              </label>
              <div className="flex items-center max-w-full h-[50px] border border-gray-500 rounded-2xl px-4 mb-6">
                <span
                  className="w-6 h-6 bg-cover filter invert"
                  style={{ backgroundImage: `url(${lockIcon})` }}
                  aria-hidden="true"
                />
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  className="w-[300px] ml-4 bg-transparent border-none text-white focus:outline-none"
                  placeholder="password"
                  required
                  autoComplete="current-password"
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </div>
          </fieldset>
        </section>

        <section aria-label="Form actions">
          {loading ? (
            <LoadingSpinner size="md" centered wrapperClassName="mb-5" aria-label="Signing in" />
          ) : (
            <button
              type="submit"
              className="flex items-center justify-center w-full h-[50px] text-white border-none rounded bg-brand-primary text-base font-bold transition duration-[0.25s] hover:cursor-pointer"
            >
              Sign in
            </button>
          )}
        </section>

        <footer>
          <p className="text-white mt-4 text-center">
            Forgot password? Click{' '}
            <button type="button" className="text-brand-primary hover:cursor-pointer" onClick={onForgotPassword}>
              here
            </button>{' '}
            to reset.
          </p>
          <p className="text-white mt-4 text-center">Contact sales for access.</p>
          <p className="text-white mt-4 text-center">
            Space-Eyes, INC is a US based company and only conducts business with US allies.
          </p>
        </footer>
      </form>
    </div>
  );
};
